import React, { useEffect, useState } from "react";
import { Wrench, Clock, Rocket } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_elite-health-members/artifacts/eb2tg2wk_cropped-Nirmal-Hospitals-3-160x54.png";

function diffParts(target) {
  const now = Date.now();
  const ms = Math.max(0, target - now);
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;
  return { ms, days, hours, minutes, seconds };
}

function Cell({ value, label }) {
  return (
    <div className="rounded-2xl bg-white/10 border border-white/20 backdrop-blur px-4 sm:px-5 py-3 sm:py-4 text-center min-w-[78px] sm:min-w-[92px]"
      data-testid={`countdown-${label.toLowerCase()}`}>
      <div className="font-display text-3xl sm:text-4xl font-bold tabular-nums text-white">
        {String(value).padStart(2, "0")}
      </div>
      <div className="mt-1 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-white/60">
        {label}
      </div>
    </div>
  );
}

function Countdown({ etaAt, onExpire }) {
  const target = new Date(etaAt).getTime();
  const [parts, setParts] = useState(() => diffParts(target));
  useEffect(() => {
    const id = setInterval(() => {
      const next = diffParts(target);
      setParts(next);
      if (next.ms === 0) {
        clearInterval(id);
        if (onExpire) onExpire();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [target, onExpire]);

  if (parts.ms === 0) {
    return (
      <div className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#C89B3C] text-[#0F2540] font-bold shadow-lg shadow-[#C89B3C]/30 animate-pulse"
        data-testid="launching-soon">
        <Rocket className="h-5 w-5" /> Launching soon…
      </div>
    );
  }

  return (
    <div className="mt-7 flex flex-wrap items-center justify-center gap-2 sm:gap-3" data-testid="maintenance-countdown">
      <Cell value={parts.days} label="Days" />
      <Cell value={parts.hours} label="Hours" />
      <Cell value={parts.minutes} label="Minutes" />
      <Cell value={parts.seconds} label="Seconds" />
    </div>
  );
}

export default function Maintenance({ message, eta, etaAt, onExpire }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F2540] via-[#1E4F87] to-[#0F2540] grid place-items-center px-6 py-12 relative overflow-hidden"
      data-testid="maintenance-page">
      {/* Subtle grid texture */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />

      <div className="relative max-w-2xl w-full text-center text-white">
        {/* Brand logo */}
        <div className="inline-flex items-center justify-center bg-white/95 rounded-xl px-5 py-3 shadow-2xl">
          <img src={LOGO_URL} alt="Nirmal Hospital" className="h-12 sm:h-14 w-auto" data-testid="maintenance-logo" />
        </div>

        <div className="mt-8 mx-auto h-14 w-14 rounded-full bg-[#C89B3C]/15 border border-[#C89B3C]/30 grid place-items-center">
          <Wrench className="h-6 w-6 text-[#C89B3C]" />
        </div>

        <div className="mt-4 text-[11px] tracking-[0.32em] uppercase text-[#C89B3C] font-semibold">
          Nirmal Privilege Card
        </div>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold">
          We&rsquo;ll be right back.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed whitespace-pre-line max-w-lg mx-auto"
          data-testid="maintenance-message">
          {message || "NPC Platform is currently under maintenance. Please check again later."}
        </p>

        {etaAt ? (
          <Countdown etaAt={etaAt} onExpire={onExpire} />
        ) : eta ? (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-sm"
            data-testid="maintenance-eta">
            <Clock className="h-4 w-4 text-[#C89B3C]" />
            {eta}
          </div>
        ) : null}

        <div className="mt-10 text-xs text-white/60">
          For urgent assistance, call the NPC helpline
          <a href="tel:+916357033301" className="block mt-1 text-base font-semibold text-white hover:text-[#C89B3C] transition-colors">
            +91-63570-33301
          </a>
        </div>

      </div>
    </div>
  );
}
